import axios, { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';

import { getApiLink } from '../api';
import useNotification from '../hooks/useNotification';

const FALLBACK_TITLE = 'news-article';
const DOWNLOAD_ERROR_MESSAGE = 'Unable to download the PDF. Please try again.';
const MAX_TITLE_LENGTH = 120;
const FILE_NAME_TIMESTAMP_FORMAT = 'YYYYMMDDHHmm';
const OBJECT_URL_TTL = 40000;
const SELF_HANDLED_STATUSES = [500, 503];

async function getNewsArticlePdf(id: number): Promise<AxiosResponse<Blob>> {
    return axios.get(getApiLink('NewsArticle/DownloadNewsArticlePdf', { id }), {
        responseType: 'blob',
        validateStatus: (status) => (status >= 200 && status < 300) || SELF_HANDLED_STATUSES.indexOf(status) !== -1
    });
}

function sanitiseTitle(title?: string | null): string {
    const cleaned = (title || '')
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_TITLE_LENGTH)
        .trim();

    return cleaned || FALLBACK_TITLE;
}

export function getPdfFileName(title?: string | null, downloadedAt: Date = new Date()): string {
    return `${sanitiseTitle(title)}${dayjs(downloadedAt).format(FILE_NAME_TIMESTAMP_FORMAT)}.pdf`;
}

function saveBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_TTL);
}

export default function useNewsArticlePdfDownload(articleId: number, articleTitle?: string | null) {
    const { addNotification } = useNotification();
    const [isDownloading, setIsDownloading] = useState(false);
    const isRequestInFlight = useRef(false);

    const downloadPdf = async () => {
        if (isRequestInFlight.current || !articleId) return;

        isRequestInFlight.current = true;
        setIsDownloading(true);

        try {
            const response = await getNewsArticlePdf(articleId);

            if (response.status >= 400 || !response.data || response.data.size === 0) {
                throw new Error(`News article PDF request failed with status ${response.status}`);
            }

            saveBlob(response.data, getPdfFileName(articleTitle));
        } catch (error) {
            addNotification({ message: DOWNLOAD_ERROR_MESSAGE, status: 'error' });
        } finally {
            isRequestInFlight.current = false;
            setIsDownloading(false);
        }
    };

    return { downloadPdf, isDownloading };
}
